import { memo } from 'react';
import Markdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { rehypeMarkRanges, type CommentRange } from './rehype-mark-ranges';
import './ChatMarkdownComponent.css';

const linkComponents: Components = {
  a: ({ href, children, ...props }) => {
    if (!href) return <a {...props}>{children}</a>;
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
        {children}
      </a>
    );
  },
};

interface ChatMarkdownProps {
  content: string;
  className?: string;
  commentRanges?: CommentRange[];
}

/**
 * Memoized so the parent's frequent re-renders (poll ticks every ~300ms while
 * streaming, sidebar toggles, compose typing) don't re-render — and therefore
 * re-parse — every ChatMarkdownComponent in the transcript. Only the part whose
 * `content` is actually growing re-parses; completed parts are skipped.
 *
 * `commentRanges` is compared structurally because the historical render path
 * builds it as a fresh array on every parent render (see ChatPage.tsx); a
 * referential check would defeat the memo. The arrays are tiny (one entry per
 * comment on the message), so this is cheap.
 */
function commentRangesEqual(a: CommentRange[] | undefined, b: CommentRange[] | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return a === b;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const pa = a[i];
    const pb = b[i];
    if (!pa || !pb || pa.id !== pb.id || pa.start !== pb.start || pa.end !== pb.end) return false;
  }
  return true;
}

export const ChatMarkdownComponent = memo(
  function ChatMarkdownComponent({ content, className, commentRanges }: ChatMarkdownProps): JSX.Element {
    const ranges = commentRanges && commentRanges.length > 0 ? commentRanges : null;

    return (
      <div className={`chat-markdown-content ${className ?? ''}`}>
        {ranges
          ? (
            <Markdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw, [rehypeMarkRanges, ranges]]}
              components={linkComponents}
            >
              {content}
            </Markdown>
          )
          : (
            <Markdown remarkPlugins={[remarkGfm]} components={linkComponents}>{content}</Markdown>
          )}
      </div>
    );
  },
  (prev, next) =>
    prev.content === next.content &&
    prev.className === next.className &&
    commentRangesEqual(prev.commentRanges, next.commentRanges),
);
